import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useAdminViews from 'src/hooks/useAdminViews';
import { Spinner } from 'src/assets/icons';
import { Wrapper, Title, Controls, Info, ActionButton } from './ControlBar.style';
import Popover from '../Popover';
import ClientFilter from '../ClientFilter';
import ClientSort from '../ClientSort';
import ViewSelector from '../ViewSelector';
import CreateViewModal from '../CreateViewModal';
import ActionsSelector from '../ActionsSelector';

const ControlBar = ({
  filters,
  onFilterChange,
  sortOption,
  sortField,
  onSortOptionChange,
  view,
  checkedCompanies,
  onViewChange,
  onActionsChange,
  viewFiltersChanged = false
}) => {
  const [isCreateViewModalOpen, setIsCreateViewModalOpen] = useState(false);
  const { createAdminView, updateAdminView, isUpdating } = useAdminViews({
    type: 'deployment_center'
  });

  const title = useMemo(() => {
    if (view) {
      return view.name;
    }
    return 'All Clients';
  }, [view]);

  const handleCreateView = useCallback(
    async name => {
      const sortOrder = sortField.startsWith('-') ? 'desc' : 'asc';
      const res = await createAdminView({
        name,
        filter_data: filters,
        sort_field: sortOrder === 'desc' ? sortField.slice(1) : sortField,
        sort_order: sortOrder
      });
      onViewChange(res);
      setIsCreateViewModalOpen(false);
    },
    [sortField, filters]
  );

  const handleUpdateView = useCallback(async () => {
    const sortOrder = sortField.startsWith('-') ? 'desc' : 'asc';
    const res = await updateAdminView({
      id: view.id,
      filter_data: filters,
      sort_field: sortOrder === 'desc' ? sortField.slice(1) : sortField,
      sort_order: sortOrder
    });
    onViewChange(res);
  }, [sortField, filters, view]);

  return (
    <>
      <CreateViewModal
        onCreate={handleCreateView}
        isOpen={isCreateViewModalOpen}
        onClose={() => setIsCreateViewModalOpen(false)}
      />
      <Wrapper>
        <Info>
          <Title>{view?.name || title}</Title>
          {view && viewFiltersChanged && (
            <ActionButton className="outlined" onClick={handleUpdateView} disabled={isUpdating}>
              {isUpdating && Spinner}
              {isUpdating ? 'Updating' : 'Update view'}
            </ActionButton>
          )}
          {!view && viewFiltersChanged && (
            <ActionButton className="outlined" onClick={() => setIsCreateViewModalOpen(true)}>
              Save View
            </ActionButton>
          )}
          {view && viewFiltersChanged && (
            <ActionButton className="text-only" onClick={() => setIsCreateViewModalOpen(true)}>
              Save as New View
            </ActionButton>
          )}
        </Info>
        <Controls>
          <ActionsSelector checkedCompanies={checkedCompanies} onClickCb={onActionsChange} />
          <ViewSelector value={view} onChange={onViewChange} type="deployment_center" />
          <Popover title="Filter">
            <ClientFilter filters={filters} onChange={onFilterChange} />
          </Popover>
          <ClientSort value={sortOption} onChange={onSortOptionChange} />
        </Controls>
      </Wrapper>
    </>
  );
};

ControlBar.defaultProps = {
  filters: {
    state: 'any',
    app: null,
    companyType: ''
  },
  viewFiltersChanged: false
};

ControlBar.propTypes = {
  filters: PropTypes.shape({
    state: PropTypes.string,
    app: PropTypes.string,
    companyType: PropTypes.string
  }),
  onFilterChange: PropTypes.func.isRequired,
  sortOption: PropTypes.string.isRequired,
  sortField: PropTypes.string.isRequired,
  onSortOptionChange: PropTypes.func.isRequired,
  view: PropTypes.number.isRequired,
  checkedCompanies: PropTypes.arrayOf(PropTypes.any).isRequired,
  onViewChange: PropTypes.func.isRequired,
  onActionsChange: PropTypes.func.isRequired,
  viewFiltersChanged: PropTypes.bool
};

export default ControlBar;
